import { useState } from "react";
import {
  Loader2,
  Plus,
  Trash2,
  Save,
  Flame,
  Clock,
  Bell,
  RefreshCw,
  Sparkles,
  Check,
  X,
  Power,
} from "lucide-react";
import {
  useScoringRules,
  useCreateScoringRule,
  useUpdateScoringRule,
  useDeleteScoringRule,
  useRecalcularScoring,
  useAlertas,
  useGenerarAlertas,
  useMarcarAlertaLeida,
  useMarcarTodasLeidas,
  useDeleteAlerta,
} from "../api";
import { useMarketingStore } from "../store";
import { MarketingHeader } from "../components/MarketingHeader";
import type { Alerta, ScoringRegla, ScoringReglaFormData } from "../types";

const CAMPOS: Record<string, string> = {
  estado: "Estado",
  email: "Email",
  telefono: "Teléfono",
  empresa: "Empresa",
  cargo: "Cargo",
  fuente: "Fuente",
  tiene_oportunidad: "Tiene oportunidad",
};
const OPERADORES: Record<string, string> = {
  no_vacio: "no está vacío",
  igual: "es igual a",
  contiene: "contiene",
  mayor: "es mayor que",
};

const SEV_CLS: Record<string, string> = {
  hot: "bg-rose-50 border-rose-200 text-rose-700",
  warning: "bg-amber-50 border-amber-200 text-amber-700",
  info: "bg-sky-50 border-sky-200 text-sky-700",
};

const EMPTY: ScoringReglaFormData = {
  nombre: "",
  campo: "estado",
  operador: "igual",
  valor: "",
  puntos: 10,
  activa: true,
};

export function ScoringPage() {
  const { emisorId } = useMarketingStore();
  const { data: reglas, isLoading: lr } = useScoringRules(emisorId);
  const { data: alertasData, isLoading: la } = useAlertas(emisorId);

  const createMut = useCreateScoringRule();
  const updateMut = useUpdateScoringRule();
  const deleteMut = useDeleteScoringRule();
  const recalcMut = useRecalcularScoring();
  const genMut = useGenerarAlertas();
  const leidaMut = useMarcarAlertaLeida();
  const todasMut = useMarcarTodasLeidas();
  const delAlertaMut = useDeleteAlerta();

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<ScoringReglaFormData>(EMPTY);

  const alertas = alertasData?.data ?? [];
  const noLeidas = alertasData?.meta?.no_leidas ?? 0;

  async function saveRegla(e: React.FormEvent) {
    e.preventDefault();
    if (!emisorId) return;
    await createMut.mutateAsync({ emisorId, payload: form });
    setCreating(false);
    setForm(EMPTY);
  }

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <MarketingHeader
        title="Marketing"
        subtitle="Lead scoring configurable y alertas inteligentes."
        right={
          <div className="flex gap-2 self-end">
            <button
              onClick={() => emisorId && genMut.mutate(emisorId)}
              disabled={!emisorId || genMut.isPending}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 px-3 py-2 font-bold text-sm transition"
            >
              {genMut.isPending ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              Generar alertas
            </button>
            <button
              onClick={() => emisorId && recalcMut.mutate(emisorId)}
              disabled={!emisorId || recalcMut.isPending}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 font-bold text-sm shadow-soft transition disabled:opacity-50"
            >
              {recalcMut.isPending ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
              Recalcular scores
            </button>
          </div>
        }
      />

      {recalcMut.data && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 text-sm font-bold shrink-0">
          {recalcMut.data.actualizados} contactos recalculados.
        </div>
      )}
      {genMut.data && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 text-sm font-bold shrink-0">
          {genMut.data.leads_calientes} leads calientes · {genMut.data.oportunidades_estancadas} oportunidades estancadas detectadas.
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ── Alertas ── */}
        <section className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden flex flex-col">
          <header className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
            <h2 className="font-black text-slate-900 flex items-center gap-2">
              <Bell size={18} className="text-brand-700" /> Bandeja de alertas
              {noLeidas > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 rounded-full bg-rose-500 text-white text-[10px] font-black px-1.5">
                  {noLeidas}
                </span>
              )}
            </h2>
            {noLeidas > 0 && (
              <button
                onClick={() => emisorId && todasMut.mutate(emisorId)}
                className="text-xs font-bold text-brand-700 hover:text-brand-900"
              >
                Marcar todas leídas
              </button>
            )}
          </header>
          <div className="flex-1 overflow-auto p-3 space-y-2 max-h-[calc(100vh-320px)]">
            {la && (
              <div className="flex items-center gap-2 text-slate-500 p-3">
                <Loader2 className="animate-spin" size={16} /> Cargando…
              </div>
            )}
            {!la && alertas.length === 0 && (
              <div className="text-center py-10 text-slate-400">
                <Bell size={32} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm font-semibold">Sin alertas</p>
                <p className="text-xs mt-1">Pulsa "Generar alertas" para detectar leads calientes.</p>
              </div>
            )}
            {alertas.map((a: Alerta) => (
              <div
                key={a.id}
                className={`rounded-xl border p-3 ${a.leida ? "bg-white border-slate-200 opacity-70" : SEV_CLS[a.severidad]}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0">
                    {a.severidad === "hot" ? (
                      <Flame size={16} className="text-rose-600 shrink-0 mt-0.5" />
                    ) : a.severidad === "warning" ? (
                      <Clock size={16} className="text-amber-600 shrink-0 mt-0.5" />
                    ) : (
                      <Bell size={16} className="text-sky-600 shrink-0 mt-0.5" />
                    )}
                    <div className="min-w-0">
                      <div className="font-bold text-sm text-slate-900">{a.titulo}</div>
                      {a.mensaje && <div className="text-xs text-slate-600 mt-0.5">{a.mensaje}</div>}
                      <div className="text-[10px] text-slate-400 mt-1">
                        {a.creado_en && new Date(a.creado_en).toLocaleString("es-ES")}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!a.leida && (
                      <button
                        onClick={() => leidaMut.mutate(a.id)}
                        title="Marcar leída"
                        className="p-1 rounded hover:bg-white/60 text-slate-500"
                      >
                        <Check size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => delAlertaMut.mutate(a.id)}
                      title="Eliminar"
                      className="p-1 rounded hover:bg-white/60 text-slate-400 hover:text-rose-600"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Reglas de scoring ── */}
        <section className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden flex flex-col">
          <header className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
            <h2 className="font-black text-slate-900 flex items-center gap-2">
              <Sparkles size={18} className="text-brand-700" /> Reglas de lead scoring
            </h2>
            <button
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-1 text-xs font-bold text-brand-700 hover:text-brand-900"
            >
              <Plus size={14} /> Nueva regla
            </button>
          </header>
          <div className="flex-1 overflow-auto max-h-[calc(100vh-320px)]">
            {lr && (
              <div className="flex items-center gap-2 text-slate-500 p-4">
                <Loader2 className="animate-spin" size={16} /> Cargando…
              </div>
            )}
            <table className="w-full text-sm">
              <tbody>
                {(reglas ?? []).map((r: ScoringRegla) => (
                  <tr key={r.id} className="border-b border-slate-100">
                    <td className="px-4 py-2.5">
                      <div className={`font-bold ${r.activa ? "text-slate-900" : "text-slate-400 line-through"}`}>
                        {r.nombre}
                      </div>
                      <div className="text-xs text-slate-500">
                        {CAMPOS[r.campo] ?? r.campo} {OPERADORES[r.operador] ?? r.operador}
                        {r.valor ? ` "${r.valor}"` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`font-mono font-black ${r.puntos >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                        {r.puntos >= 0 ? "+" : ""}{r.puntos}
                      </span>
                    </td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => updateMut.mutate({ id: r.id, payload: { activa: !r.activa } })}
                          title={r.activa ? "Desactivar" : "Activar"}
                          className={`p-1.5 rounded ${r.activa ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"}`}
                        >
                          <Power size={14} />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Eliminar regla "${r.nombre}"?`)) deleteMut.mutate(r.id);
                          }}
                          className="p-1.5 rounded text-slate-300 hover:bg-rose-50 hover:text-rose-600"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {reglas && reglas.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-slate-400">
                      Sin reglas. Crea la primera.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-400">
            Tras editar reglas, pulsa "Recalcular scores" para aplicarlas a todos los contactos.
          </div>
        </section>
      </div>

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={saveRegla} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <header className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black text-slate-900">Nueva regla de scoring</h2>
              <button type="button" onClick={() => setCreating(false)} className="text-slate-400 hover:text-slate-700">
                <X size={20} />
              </button>
            </header>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Nombre *</label>
                <input
                  required
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder="ej: Cargo decisor"
                  className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Campo</label>
                  <select
                    value={form.campo}
                    onChange={(e) => setForm({ ...form, campo: e.target.value })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    {Object.entries(CAMPOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Operador</label>
                  <select
                    value={form.operador}
                    onChange={(e) => setForm({ ...form, operador: e.target.value })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  >
                    {Object.entries(OPERADORES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Valor</label>
                  <input
                    type="text"
                    value={form.valor ?? ""}
                    onChange={(e) => setForm({ ...form, valor: e.target.value })}
                    placeholder={form.operador === "no_vacio" ? "(no aplica)" : "ej: cualificado"}
                    disabled={form.operador === "no_vacio"}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-50"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Puntos</label>
                  <input
                    type="number"
                    value={form.puntos}
                    onChange={(e) => setForm({ ...form, puntos: Number(e.target.value) })}
                    className="w-full mt-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-bold hover:bg-slate-50">
                Cancelar
              </button>
              <button type="submit" disabled={createMut.isPending} className="inline-flex items-center gap-2 rounded-lg bg-brand-700 hover:bg-brand-800 text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
                <Save size={14} /> Crear regla
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
